/* eslint-disable max-lines */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import { PayPal, AdyenCheckout, Dropin, Card, ApplePay } from '@adyen/adyen-web';
import logger from '@tcp/core/src/utils/loggerInstance';
import { INITIALIZE_PAYPAL_BUTTON } from '@tcp/core/src/constants/brandSpecific.constants';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import '@adyen/adyen-web/styles/adyen.css';
import { getIconPath, getBrandInCaps, getSiteId } from '@tcp/core/src/utils/utils';
import { getAPIConfig, getViewportInfo, readCookie } from '@tcp/core/src/utils';
import { getDynamicLabelValues } from '@tcp/core/src/utils/labels/labelUtilty';
import CheckoutConstants from '@tcp/core/src/components/features/CnC/Checkout/Checkout.constants.js';
import {
  getShippingMethods,
  setShippingMethodAndAddressId,
} from '@tcp/core/src/services/abstractors/CnC/CheckoutSFCC';
import {
  getCurrentOrderFormatter,
  getCartCachedDataApplePay,
} from '../../../../../services/abstractors/SFCC/CartItemTile';
import CONSTANTS from './AdyenPayment.constants';
import { updatePayPalOrder, validateMerchant } from '../../../../../services/abstractors/CnC/Adyen';
import styles from '../styles/AdyenPayment.style';
import utility from '../../../../features/CnC/Checkout/util/utility';
import { CHECKOUT_ROUTES } from '../../../../features/CnC/Checkout/Checkout.constants';
import { getLocalStorage } from '../../../../../utils/localStorageManagement';
import {
  getLineItems,
  formatShippingMethods,
  getLedgerSummaryData,
  getShippingUpdatePayloadWeb,
  addAddressAndShippingMethod,
  getTotal,
  formatShippingMethodsForBopisBoss,
  verifyMellisaAddress,
} from '../../../atoms/ApplePaymentButton/container/ApplePaymentButton.util';

const orderTypeKey = 'data-ordertype';
export const getCurrency = (countryCode) =>
  CONSTANTS?.currencies[countryCode] || CONSTANTS?.currencies.default;

function parseAmount(amount, countryCode) {
  return {
    value: Number(amount),
    currency: getCurrency(countryCode),
  };
}

const removeDecimal = (number) => {
  return Math.floor(number * 100);
};

const getDeliveryAddressPayload = (deliveryAddress) => {
  const { street, city, postalCode, countryCode, state } = deliveryAddress || {};

  if (!postalCode || !countryCode || !state) {
    return null;
  }
  return {
    address1: street,
    city,
    postalCode,
    countryCode,
    stateCode: state,
  };
};

const getShippingAddressForUpdate = (deliveryAddress) => {
  const { street, city, postalCode, country, stateOrProvince } = deliveryAddress || {};

  if (!postalCode || !country || !stateOrProvince) {
    return null;
  }
  return {
    address1: street,
    city,
    postalCode,
    countryCode: country,
    stateCode: stateOrProvince,
  };
};

const getFirstAvailableValue = (primary, fallback, backup) => primary || fallback || backup || '';

export const getBillingAddressForApplePay = ({ billingContact = {}, shippingContact = {} }) => {
  const { DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY: dummy } = CONSTANTS;

  return {
    address1: getFirstAvailableValue(
      billingContact?.addressLines?.[0],
      shippingContact?.addressLines?.[0],
      dummy.address1
    ),
    address2: getFirstAvailableValue(
      billingContact?.addressLines?.[1],
      shippingContact?.addressLines?.[1]
    ),
    city: getFirstAvailableValue(billingContact?.locality, shippingContact?.locality, dummy.city),
    postalCode: getFirstAvailableValue(
      billingContact?.postalCode,
      shippingContact?.postalCode,
      dummy.postalCode
    ),
    countryCode: getFirstAvailableValue(
      billingContact?.countryCode?.toUpperCase?.(),
      shippingContact?.countryCode?.toUpperCase?.(),
      dummy.countryCode
    ),
    stateCode: getFirstAvailableValue(
      billingContact?.administrativeArea?.toUpperCase?.(),
      shippingContact?.administrativeArea?.toUpperCase?.(),
      dummy.stateCode
    ),
    c_emailAddress: getFirstAvailableValue(
      billingContact?.emailAddress,
      shippingContact?.emailAddress,
      dummy.email
    ),
    firstName: getFirstAvailableValue(
      billingContact?.givenName,
      shippingContact?.givenName,
      dummy.firstName
    ),
    lastName: getFirstAvailableValue(
      billingContact?.familyName,
      shippingContact?.familyName,
      dummy.lastName
    ),
    phone: getFirstAvailableValue(
      billingContact?.phoneNumber,
      shippingContact?.phoneNumber,
      dummy.phone
    ),
  };
};

export const getPickUpAddressForApplePay = ({ pickupStoreDetails, billingAddress }) => {
  const { DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY: dummy } = CONSTANTS;
  const shippingAddress = pickupStoreDetails?.shippingAddress || {};
  return {
    c_altEmail: pickupStoreDetails?.c_altEmail || '',
    c_altName: pickupStoreDetails?.c_altName || '',
    c_vibesSmsPhoneNo: pickupStoreDetails?.c_vibesSmsPhoneNo || '',
    c_phoneCountryCode: pickupStoreDetails?.c_phoneCountryCode || '',
    email: getFirstAvailableValue(
      billingAddress?.c_emailAddress,
      shippingAddress?.c_emailAddress,
      dummy.email
    ),
    firstName: getFirstAvailableValue(
      billingAddress?.firstName,
      shippingAddress?.firstName,
      dummy.firstName
    ),
    lastName: getFirstAvailableValue(
      billingAddress?.lastName,
      shippingAddress?.lastName,

      dummy.lastName
    ),
    phone: getFirstAvailableValue(billingAddress?.phone, shippingAddress?.phone, dummy.phone),
  };
};

const getBillingAddress = (billingAddress, authorizedEvent, deliveryAddress) => {
  const { DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY } = CONSTANTS;

  return {
    address1:
      billingAddress?.street ||
      deliveryAddress?.street ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.address1,
    address2: '',
    city: billingAddress?.city || deliveryAddress?.city || DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.city,
    postalCode:
      billingAddress?.postalCode ||
      deliveryAddress?.postalCode ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.postalCode,
    countryCode: billingAddress?.country,
    stateCode:
      billingAddress?.stateOrProvince ||
      deliveryAddress?.stateOrProvince ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.stateCode,
    c_emailAddress:
      authorizedEvent?.payer?.email_address || DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.email,
    firstName:
      authorizedEvent?.payer?.name?.given_name || DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.firstName,
    lastName: authorizedEvent?.payer?.name?.surname || DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.lastName,
    phone:
      authorizedEvent?.payer?.phone?.phone_number?.national_number ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.phone,
  };
};

const getShippingAddress = (
  deliveryAddress,
  authorizedEvent,
  shippingContact,
  userProfileInfo,
  emailAddressFromSS
) => {
  const { DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY } = CONSTANTS;
  const { firstName: initialFirstName, lastName: initialLastName } = deliveryAddress || {};
  let firstName = initialFirstName || '';
  let lastName = initialLastName || '';
  if (initialFirstName && !initialLastName) {
    const nameParts = initialFirstName.trim().split(' ');

    if (nameParts.length > 1) {
      lastName = nameParts.pop(); // Get the last part as last name
      firstName = nameParts.join(' '); // Join remaining parts as first name
    } else {
      // If there's only one part, set both to that part
      firstName = nameParts[0] || '';
      lastName = nameParts[0] || '';
    }
  }

  const profileInfo = JSON.parse(JSON.stringify(userProfileInfo));

  return {
    address1: deliveryAddress?.street,
    address2: '',
    city: deliveryAddress?.city,
    postalCode: deliveryAddress?.postalCode,
    countryCode: deliveryAddress?.country,
    stateCode: deliveryAddress?.stateOrProvince,
    c_emailAddress:
      shippingContact?.emailAddress ||
      profileInfo?.emailAddress ||
      emailAddressFromSS ||
      authorizedEvent?.payer?.email_address ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.email,
    firstName,
    lastName,
    phone:
      shippingContact?.phoneNumber ||
      profileInfo?.phoneNumber ||
      authorizedEvent?.payer?.phone?.phone_number?.national_number ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.phone,
  };
};

const extractFirstAndLastName = (deliveryAddress) => {
  const { firstName: initialFirstName, lastName: initialLastName } = deliveryAddress || {};
  let firstName = initialFirstName || '';
  let lastName = initialLastName || '';
  if (initialFirstName && !initialLastName) {
    const nameParts = initialFirstName.trim().split(' ');

    if (nameParts.length > 1) {
      lastName = nameParts.pop(); // Get the last part as last name
      firstName = nameParts.join(' '); // Join remaining parts as first name
    } else {
      // If there's only one part, set both to that part
      firstName = nameParts[0] || '';
      lastName = nameParts[0] || '';
    }
  }

  return { firstName, lastName };
};

const getPickUpDetails = (
  authorizedEvent,
  userProfileInfo,
  userBasketPickUpValues,
  userBasketPickUpAlternatePerson,
  deliveryAddress,
  emailAddressFromSS
) => {
  const { DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY } = CONSTANTS;
  const profileInfo = JSON.parse(JSON.stringify(userProfileInfo));

  const { firstName, lastName } = extractFirstAndLastName(deliveryAddress);

  return {
    email:
      userBasketPickUpValues?.emailAddress ||
      profileInfo?.emailAddress ||
      emailAddressFromSS ||
      authorizedEvent?.payer?.email_address ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.email,
    firstName:
      firstName ||
      profileInfo?.firstName ||
      authorizedEvent?.payer?.name?.given_name ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.firstName,
    lastName:
      lastName ||
      profileInfo?.lastName ||
      authorizedEvent?.payer?.name?.surname ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.lastName,
    phone:
      userBasketPickUpValues?.phoneNumber ||
      profileInfo?.phoneNumber ||
      authorizedEvent?.payer?.phone?.phone_number?.national_number ||
      DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY.phone,
    c_altEmail: userBasketPickUpAlternatePerson?.emailAddress,
    c_altName: userBasketPickUpAlternatePerson?.firstName,
    c_vibesSmsPhoneNo: '',
    c_phoneCountryCode: userBasketPickUpValues?.phoneCountryCode,
  };
};

const constructCreateOrderPayload = ({
  session,
  state,
  billingAddress,
  authorizedEvent,
  deliveryAddress,
  isPureBossOrBopis,
  shippingContact,
  userProfileInfo,
  userBasketPickUpValues,
  userBasketPickUpAlternatePerson,
  emailAddressFromSS,
  orderPspReference,
  newPaymentData,
}) => {
  const payload = {
    sessionData: session?.sessionData,
    paymentData: newPaymentData,
    billingAddress: getBillingAddress(billingAddress, authorizedEvent, deliveryAddress),
  };
  // Initialize details only if orderPspReference is present
  if (orderPspReference) {
    payload.details = {
      ...state?.data?.details,
      pspReference: orderPspReference,
    };
  } else {
    payload.details = state?.data?.details; // Assign as is if orderPspReference is absent
  }

  if (isPureBossOrBopis) {
    payload.pickUpDetailsDto = getPickUpDetails(
      authorizedEvent,
      userProfileInfo,
      userBasketPickUpValues,
      userBasketPickUpAlternatePerson,
      deliveryAddress,
      emailAddressFromSS
    );
  } else {
    payload.shippingAddress = getShippingAddress(
      deliveryAddress,
      authorizedEvent,
      shippingContact,
      userProfileInfo,
      emailAddressFromSS
    );
  }

  return payload;
};

const constructCreateOrderPayloadApplePay = ({
  applePayToken,
  shippingAddress,
  isBagPage,
  billingAddress,
  shipments,
  isPureBossOrBopis,
}) => {
  const pickupStoreDetails = shipments.find((shipment) => shipment.shipmentId === 'me2');

  const payload = {
    details: {
      applePayToken,
      paymentSource: 'applepay',
      returnUrl: isBagPage ? 'CART_PAGE' : 'CHECKOUT_PAGE',
    },
    billingAddress,
  };

  if (isPureBossOrBopis) {
    payload.pickUpDetailsDto = getPickUpAddressForApplePay({
      pickupStoreDetails,
      billingAddress,
    });
  } else if (shippingAddress) {
    payload.shippingAddress = shippingAddress;
  }

  return payload;
};

const disableShippingAddressChange = ({ componentRef, shipments, orderType }) => {
  try {
    const ref = componentRef?.current;
    const orderTypeValue = ref?.getAttribute(orderTypeKey) || orderType;
    if (orderTypeValue === 'ECOM_MIX') return false;
    const shipmentsValue =
      (shipments && ref && JSON.parse(ref?.getAttribute('data-shipments'))) || shipments;
    const isPureEcom = orderTypeValue === 'PURE_ECOM';
    const shipment = shipmentsValue?.find((ship) =>
      isPureEcom ? ship.shipmentId === 'me' : ship.shipmentId !== 'me'
    );
    return !!shipment?.shippingAddress?.address1;
  } catch (e) {
    logger.error('Adyen paypal error in disableShippingAddressChange:', e);
    return false;
  }
};

const handleCreateOrder = ({
  submitReview,
  adyenRequest,
  component,
  isBagPage,
  isMiniBag,
  actions,
  fromAdyenApplePay,
  isAddressValid,
  shouldBlock,
  reInitializePayPal,
}) => {
  const sfsError = JSON.parse(getLocalStorage('sfsError'));
  if (sfsError && isBagPage) {
    if (component && typeof component.reject === 'function') {
      component.reject();
    }
    utility.routeToPage(CHECKOUT_ROUTES.singlePageCheckoutPagePaypal);
  } else {
    submitReview({
      adyenRequest,
      adyenComponent: component,
      fromAdyenPayPal: true,
      isMiniBag,
      actions,
      fromAdyenApplePay,
      isAddressValid,
      shouldBlock,
      reInitializePayPal,
    });
  }
};

const fetchShippingMethods = async ({
  shippingContact,
  shippingData,
  setCartLoading,
  cachedShippingMethods,
}) => {
  const {
    errorLabels = [],
    shipModeId = '',
    shippingTotal = 0,
    isDynamicFreeShippingEnabled = false,
    shipments = [],
    freeShippingThershold = 0,
    orderType = '',
    productTotal = 0,
  } = shippingData || {};
  const basketId = readCookie('basketId');
  const zipCode = shippingContact?.postalCode;
  const state = shippingContact?.administrativeArea?.toUpperCase?.();
  if (
    shippingContact?.administrativeArea !== undefined &&
    shippingContact?.administrativeArea !== null &&
    typeof shippingContact?.administrativeArea !== 'string'
  ) {
    logger.error('Error in Adyen fetchShippingMethod', {
      extraData: {
        component: 'Adyen Payment- fetchShippingMethods',
        administrativeAreaType: typeof shippingContact?.administrativeArea,
        administrativeArea:
          shippingContact?.administrativeArea &&
          JSON.stringify(shippingContact?.administrativeArea),
        zipCode,
      },
    });
  }
  const payload = {
    state,
    zipCode,
    fromPage: 'applePaySheet',
  };
  const eventType =
    zipCode || state
      ? CheckoutConstants.CHANGE_SHIPMMENT_METHODS
      : CheckoutConstants.LOAD_SHIPMMENT_METHODS;
  let shipmentMethods = [];
  try {
    if (cachedShippingMethods) {
      shipmentMethods = cachedShippingMethods;
    } else {
      setCartLoading(true);
      shipmentMethods = await getShippingMethods(payload, errorLabels, eventType, basketId);
    }
  } catch (error) {
    console.error('Error fetching shipping methods:', error);
  }
  const newShippingMethods = formatShippingMethods(
    shipmentMethods,
    shipModeId,
    shippingTotal,
    isDynamicFreeShippingEnabled,
    shipments,
    freeShippingThershold,
    orderType,
    productTotal
  );
  return { newShippingMethods, shipmentMethods };
};

const checkMethodCondition = (res) => {
  return isEmpty(res.shipments[0].shippingMethod);
};

const handleShippingUpdate = async (data, newShippingMethods, resolve) => {
  const cart = await getCartCachedDataApplePay();
  let newLedgerSummaryData = null;
  if (checkMethodCondition(data.updateShipping)) {
    const payload = newShippingMethods[0].identifier;
    const methodResp = await setShippingMethodAndAddressId(payload);
    if (methodResp.success) {
      const updatedCart = await getCartCachedDataApplePay();
      const response = {
        coupons: {},
        orderDetails: getCurrentOrderFormatter(updatedCart),
      };
      newLedgerSummaryData = getLedgerSummaryData(response);
    }
  } else {
    const response = {
      coupons: {},
      orderDetails: getCurrentOrderFormatter(cart),
    };
    newLedgerSummaryData = getLedgerSummaryData(response);
  }
  resolve({
    newTotal: getTotal(newLedgerSummaryData.orderBalanceTotal, 'final'),
    newLineItems: getLineItems(newLedgerSummaryData),
    newShippingMethods,
  });
};

const handleShippingContactSelected = async ({
  event,
  shippingContact,
  shippingData,
  userBasketPickUpValues,
  resolve,
  reject,
  setCartLoading,
}) => {
  const newShippingContact = event.shippingContact || shippingContact;
  const { orderType } = shippingData;
  try {
    setCartLoading(true);
    const { newShippingMethods } = await fetchShippingMethods({
      shippingContact: newShippingContact,
      shippingData,
      setCartLoading,
    });
    const shippingObject = getShippingUpdatePayloadWeb(event, orderType, userBasketPickUpValues);
    const res = await addAddressAndShippingMethod(shippingObject, orderType);
    if (res.data) {
      await handleShippingUpdate(res.data, newShippingMethods, resolve);
    }
  } catch (error) {
    console.error('Error in fetching Shipping Methods ', error);
    reject(error);
  }
};

function convertToMelissaAddressFormat(input) {
  return {
    addressId: input?.addressId || '', // optional fallback
    addressLines: [input?.street || ''],
    administrativeArea: input?.stateOrProvince || '',
    c_validationCode: input?.c_validationCode || '', // optional
    countryCode: input?.country || '',
    emailAddress: input?.emailAddress || '',
    familyName: input?.lastName || '',
    givenName: input?.firstName || '',
    locality: input?.city || '',
    phoneNumber: input?.phoneNumber || '',
    postalCode: input?.postalCode || '',
  };
}

const constructContact = (billingContact, shippingContact) => {
  const { DUMMY_ADDRESS_VALUES_FOR_AFTER_PAY: dummy } = CONSTANTS;
  return {
    address1: billingContact.addressLines?.[0] || '',
    address2: billingContact.addressLines?.[1] || '',
    city: billingContact.locality || '',
    postalCode: billingContact.postalCode || '',
    countryCode: billingContact.countryCode?.toUpperCase() || '',
    stateCode: billingContact.administrativeArea?.toUpperCase() || '',
    c_emailAddress: billingContact?.emailAddress || shippingContact?.emailAddress || '',
    firstName: billingContact.givenName,
    lastName: billingContact.familyName || dummy.lastName || '',
    phone: billingContact?.phoneNumber || shippingContact?.phoneNumber || '',
  };
};

// eslint-disable-next-line complexity
const handleOnSubmit = async ({
  latestAuthorizedEvent,
  shippingContact,
  isBagPage,
  isMiniBag,
  shipments,
  isPureBossOrBopis,
  submitReview,
  component,
  actions,
  fromAdyenApplePay,
  state,
  isOptimizeApplePaySheetEnabled,
  shouldBlock,
}) => {
  try {
    if (!latestAuthorizedEvent) {
      throw new Error('Apple Pay authorized event is missing');
    }
    const validationResult = await verifyMellisaAddress(
      false,
      convertToMelissaAddressFormat(state?.data?.deliveryAddress)
    );
    if (isOptimizeApplePaySheetEnabled && !isBagPage) validationResult.status = true;
    const { payment } = latestAuthorizedEvent || {};
    const billingContact = payment?.shippingContact || payment?.billingContact;
    const token = payment?.token;
    const contact = constructContact(billingContact, shippingContact);

    const formattedBillingAddress = getBillingAddressForApplePay({
      billingContact,
      shippingContact,
    });

    const adyenRequest = constructCreateOrderPayloadApplePay({
      applePayToken: btoa(JSON.stringify(token.paymentData)),
      shippingAddress: contact,
      isBagPage,
      billingAddress: formattedBillingAddress,
      shipments,
      isPureBossOrBopis,
    });

    handleCreateOrder({
      submitReview,
      adyenRequest,
      component,
      isBagPage,
      isMiniBag,
      actions,
      fromAdyenApplePay,
      isAddressValid: validationResult?.status || false,
      shouldBlock,
    });
    // This should be triggered only when create order is successful
  } catch (error) {
    logger.error('Apple Pay onSubmit/createOrder failed: ', {
      error,
      extraData: {
        component: 'Adyen Payment - handleOnSubmit',
        payloadData: component,
      },
    });
    actions.reject();
  }
};

const handleShippingMethodSelected = async ({ event, resolve }) => {
  const payload = event.shippingMethod.identifier;
  const res = await setShippingMethodAndAddressId(payload);
  if (res) {
    const cart = await getCartCachedDataApplePay();
    const resp = {
      coupons: {},
      orderDetails: getCurrentOrderFormatter(cart),
    };

    const newLedgerSummaryData = getLedgerSummaryData(resp);

    resolve({
      newTotal: {
        label: 'final',
        amount: newLedgerSummaryData.orderBalanceTotal,
        type: 'final',
      },
      newLineItems: getLineItems(newLedgerSummaryData),
    });
  }
};

const getContactFields = (boosBopisOrderType, suppressflag) => {
  if (boosBopisOrderType) {
    return suppressflag ? ['name', 'phone'] : ['name', 'phone', 'email'];
  }
  return suppressflag
    ? ['postalAddress', 'name', 'phone']
    : ['postalAddress', 'name', 'phone', 'email'];
};

const resetComponentStatusIfDropIn = (component) => {
  if (
    (component?.props?.type === 'dropin' || component?.props?.type === 'applepay') &&
    typeof component.setStatus === 'function'
  ) {
    component.setStatus('ready');
  }
};

const getApplePayConfiguration = ({
  heightValue,
  componentRef,
  isBagPage,
  pageLedgerSummaryData,
  shippingContact,
  shippingMethods,
  userBasketPickUpValues,
  isMiniBag,
  submitReview,
  shippingData,
  shipments,
  suppressEmailField,
  stores,
  isOptimizeApplePaySheetEnabled, // New killswitch parameter
  bossGCAndPreOrderCaseRef,
  isBackPackAloneErrorRef,
  verifyAllExpiredCouponsRef,
  isBackPackOOSRef,
  setCartLoading,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  let shouldBlock = false;
  let latestAuthorizedEvent = null;
  const orderTypeValue =
    componentRef?.current?.getAttribute(orderTypeKey) || shippingData?.orderType;
  const isPureBossOrBopis = orderTypeValue === 'PURE_BOSS' || orderTypeValue === 'PURE_BOPIS';

  // Debug formatted shipping methods
  const formattedMethods = isPureBossOrBopis
    ? formatShippingMethodsForBopisBoss(stores, true)
    : shippingMethods;

  // Create base configuration that's common to all scenarios
  const baseConfig = {
    style: { height: heightValue },
    buttonType: isBagPage ? 'plain' : 'buy',
    buttonColor: isBagPage && 'white-outline',
    total: getTotal(pageLedgerSummaryData.orderBalanceTotal, 'final'),
    lineItems: getLineItems(pageLedgerSummaryData),
    isExpress: true,
    disableFinalAnimation: true,
    disableSuccessAnimation: true,
    onValidateMerchant: (resolve, reject) => {
      console.log('[Adyen Apple Pay-onValidateMerchant]', resolve, reject);
      validateMerchant().then(resolve).catch(reject);
    },
    onClick: async (resolve, reject) => {
      try {
        console.log('[Adyen Apple Pay-onClick]', resolve, reject);
        resolve();
        shouldBlock =
          !isBackPackAloneErrorRef.current &&
          !isBackPackOOSRef.current &&
          (bossGCAndPreOrderCaseRef.current || verifyAllExpiredCouponsRef.current);
      } catch (e) {
        logger.error('[Adyen Apple Pay-onClick]', e);
        reject();
        throw e;
      }
    },
    onAuthorized: ({ authorizedEvent }, actions) => {
      latestAuthorizedEvent =
        isOptimizeApplePaySheetEnabled && !isBagPage
          ? {
              ...authorizedEvent,
              payment: {
                ...authorizedEvent.payment,
                billingContact: shippingContact, // Ensure billingContact exists in the authorized event
              },
            }
          : authorizedEvent;
      actions.resolve();
      console.log('[Adyen Apple Pay-onAuthorized]', actions);
    },
    onSubmit: (state, component, actions) => {
      handleOnSubmit({
        latestAuthorizedEvent,
        shippingContact,
        isBagPage,
        isMiniBag,
        shipments,
        isPureBossOrBopis,
        submitReview,
        component,
        actions,
        fromAdyenApplePay: true,
        state,
        isOptimizeApplePaySheetEnabled,
        shouldBlock,
      });
    },
    onApplePayCodeClose: () => {},
    onError: (error, component) => {
      logger.error('[Adyen Apple Pay-onError]', error, component);
      setCartLoading(false);
      resetComponentStatusIfDropIn(component);
    },
    onPaymentFailed: (result, component) => {
      logger.error('[Adyen Apple Pay-onPaymentFailed]', result, component);
      setCartLoading(false);
      resetComponentStatusIfDropIn(component);
    },
    onPaymentCompleted: (result, component) => {
      logger.error('[Adyen Apple Pay-onPaymentCompleted]', result, component);
      setCartLoading(false);
      resetComponentStatusIfDropIn(component);
    },
  };

  // For optimized checkout (killswitch ON and NOT bag page)
  if (isOptimizeApplePaySheetEnabled && !isBagPage) {
    return baseConfig;
  }

  // Full configuration for all other cases (bag page or killswitch OFF)
  return {
    ...baseConfig,
    shippingType: isPureBossOrBopis ? 'storePickup' : 'shipping',
    requiredShippingContactFields: getContactFields(isPureBossOrBopis, suppressEmailField),
    billingContact: shippingContact,
    shippingContact,
    shippingMethods: Array.isArray(formattedMethods) ? formattedMethods : [formattedMethods],
    onShippingContactSelected: (resolve, reject, event) =>
      handleShippingContactSelected({
        event,
        shippingContact,
        shippingData,
        userBasketPickUpValues,
        resolve,
        reject,
        setCartLoading,
      }),
    onShippingMethodSelected: (resolve, _reject, event) =>
      handleShippingMethodSelected({ event, resolve }),
  };
};

const setErrorMessages = (error, errorLabels, setServerErrorCheckoutAction, setAddToCartError) => {
  const genericErrMsg = getDynamicLabelValues(
    'dynamic.error_messages.lbl_errorMessages_ERR_GENERIC_UPDATEORDER'
  );

  const errCode = error?.errorCode;
  const errMsg = errorLabels[errCode] || genericErrMsg;
  setServerErrorCheckoutAction({
    errorMessage: errMsg,
    component: 'PAGE',
  });
  setAddToCartError(errMsg);
};

const reInitializePayPal = (component, loadAdyen, session) => {
  component?.unmount?.();
  loadAdyen(session);
};

const getPayPalConfiguration = ({
  heightValue,
  componentRef,
  orderType,
  session,
  isBagPage,
  startAdyenPayment,
  submitReview,
  setServerErrorCheckoutAction,
  setAddToCartError,
  showLoader,
  setIsMixedPaymentWithPaypalAction,
  setIsPaypalClicked,
  reloadDropinPaypal,
  adyenIntentValue,
  isMiniBag,
  shippingContact,
  userProfileInfo,
  userBasketPickUpValues,
  userBasketPickUpAlternatePerson,
  errorLabels,
  shipments,
  emailAddressFromSS,
  toggleMessageModal,
  bossGCAndPreOrderCaseRef,
  isBackPackAloneErrorRef,
  verifyAllExpiredCouponsRef,
  isBackPackOOSRef,
  loadAdyen,
  isSupressPaypalCrossCountryShipping,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  let orderPspReference = null;
  let addressObject = null;
  let disableOnShippingChange = null;
  let shouldBlock = false;
  return {
    style: {
      layout: 'vertical',
      shape: INITIALIZE_PAYPAL_BUTTON[getBrandInCaps()],
      color: 'white',
      tagline: false,
      height: heightValue,
      label: isBagPage ? 'paypal' : 'pay',
    },
    isExpress: true,
    cspNonce: 'MY_CSP_NONCE',
    intent: adyenIntentValue,
    blockPayPalCreditButton: true,
    blockPayPalPayLaterButton: true,
    blockPayPalVenmoButton: true,

    onClick: () => {
      shouldBlock =
        !isBackPackAloneErrorRef.current &&
        !isBackPackOOSRef.current &&
        (bossGCAndPreOrderCaseRef.current || verifyAllExpiredCouponsRef.current);
      if (setIsPaypalClicked) setIsPaypalClicked(true);
      disableOnShippingChange = disableShippingAddressChange({
        componentRef,
        shipments,
        orderType,
      });
      console.error(`Adyen onClick orderType: ${orderType}`);
    },

    onSubmit: async (state, component, actions) => {
      console.error(`Adyen onSubmit shouldBlock: ${shouldBlock}, isBagPage: ${isBagPage}`);

      const payload = {
        data: state.data,
        actions,
        isBagPage,
        component,
      };
      try {
        if (shouldBlock && isBagPage) {
          component?.reject?.();
          actions.reject();
          reloadDropinPaypal();
          toggleMessageModal();
          return;
        }
        const result = await startAdyenPayment(payload);
        const { resultCode, action, order, donationToken, pspReference } = result;
        orderPspReference = pspReference;

        actions.resolve({
          resultCode,
          action,
          order,
          donationToken,
        });
      } catch (error) {
        console.error('Error during payment:', error);
        if (component && typeof component.reject === 'function') {
          component.reject();
        }
        actions.reject();
        if (setIsPaypalClicked) setIsPaypalClicked(false);

        if (error?.errorCode === 'ERR_ADYEN_MIX_PAYMENT_NOT_ALLOWED') {
          setTimeout(() => {
            setIsMixedPaymentWithPaypalAction(true);
          }, 500); // Small delay to ensure component rejection completes first
        }
      }
    },

    onShippingAddressChange: async (data, actions, component) => {
      console.error('Adyen onShippingAddressChange called');
      if (
        !isSupressPaypalCrossCountryShipping &&
        data?.shippingAddress?.countryCode?.toLowerCase?.() !== getSiteId()
      ) {
        console.error('Adyen onShippingAddressChange cross contry shipping');
        setErrorMessages(
          { errorCode: 'ERR_CROSS_SITE_ADDRESS_NOT_SUPPORTED' },
          errorLabels,
          setServerErrorCheckoutAction,
          setAddToCartError
        );

        return actions.reject(data.errors.COUNTRY_ERROR);
      }
      try {
        showLoader(true);
        const userHasAddressInBasket = disableShippingAddressChange({
          componentRef,
          shipments,
          orderType,
        });

        if (disableOnShippingChange) {
          disableOnShippingChange = false;
        } else if (disableOnShippingChange === false && userHasAddressInBasket) {
          return actions.reject();
        }

        const shippingAddress = data?.shippingAddress
          ? getDeliveryAddressPayload(data.shippingAddress)
          : null;

        const patch = {
          paymentData: component.paymentData,
          pspReference: orderPspReference,
        };
        if (shippingAddress) {
          patch.shippingAddress = shippingAddress;
        } else {
          return actions.reject();
        }
        const { data: responseData } = await updatePayPalOrder(patch);
        if (responseData.paymentData) {
          component.updatePaymentData(responseData.paymentData);
        } else {
          const error = responseData && responseData.errors && responseData.errors[0];
          setErrorMessages(error, errorLabels, setServerErrorCheckoutAction, setAddToCartError);
          console.error('Adyen shipping address update failed');
          return actions.reject();
        }
      } catch (error) {
        setErrorMessages(error, errorLabels, setServerErrorCheckoutAction, setAddToCartError);
        console.error('Adyen error in shipping address update:', error);
        return actions.reject();
      }
      return null;
    },
    onShippingOptionsChange: () => {},
    onAuthorized: (data, actions) => {
      // Required - to call this method to get adress data in onAdditionalDetails callback
      addressObject = data;
      showLoader(true);
      actions.resolve();
    },

    onAdditionalDetails: async (state, component, actions) => {
      console.error('Adyen onAdditionalDetails called');
      const hasValidState = !isEmpty(component.state);
      let newPaymentData = null;
      const {
        authorizedEvent,
        billingAddress = {},
        deliveryAddress,
      } = hasValidState ? component.state : addressObject;
      const shippingAddress = deliveryAddress ? getShippingAddressForUpdate(deliveryAddress) : null;
      const patch = {
        paymentData: component.paymentData ? component.paymentData : state?.data?.paymentData,
        pspReference: orderPspReference,
        shippingAddress,
      };
      try {
        const { data: responseData } = await updatePayPalOrder(patch);
        if (responseData && responseData.paymentData) {
          console.error('Adyen onAdditionalDetails update successful');
          newPaymentData = responseData.paymentData;
        } else {
          const error = responseData && responseData.errors && responseData.errors[0];
          setErrorMessages(error, errorLabels, setServerErrorCheckoutAction, setAddToCartError);
          console.error('Adyen onAdditionalDetails update failed due to missing paymentData');
          showLoader(false);
          actions.reject();
          return;
        }
      } catch (error) {
        setErrorMessages(error, errorLabels, setServerErrorCheckoutAction, setAddToCartError);
        console.error('Adyen error in onAdditionalDetails update order:', error);
        showLoader(false);
        actions.reject();
        return;
      }
      showLoader(true);

      const orderTypeValue = componentRef?.current?.getAttribute(orderTypeKey) || orderType;
      const isPureBossOrBopis = orderTypeValue === 'PURE_BOSS' || orderTypeValue === 'PURE_BOPIS';
      const adyenRequest = constructCreateOrderPayload({
        session,
        state,
        billingAddress,
        authorizedEvent,
        deliveryAddress,
        isPureBossOrBopis,
        shippingContact,
        userProfileInfo,
        userBasketPickUpValues,
        userBasketPickUpAlternatePerson,
        emailAddressFromSS,
        orderPspReference,
        newPaymentData,
      });

      handleCreateOrder({
        submitReview,
        adyenRequest,
        component,
        isBagPage,
        isMiniBag,
        shouldBlock,
        actions,
        reInitializePayPal: () => reInitializePayPal(component, loadAdyen, session),
      });
    },

    onPaymentFailed: (result, element) => {
      if (setIsPaypalClicked) setIsPaypalClicked(false);
      showLoader(false);
      console.error('Adyen onPaymentFailed callback called ', result);

      if (element?.type === 'dropin') {
        reloadDropinPaypal();
      } else {
        reInitializePayPal(element, loadAdyen, session);
      }
      if (element && typeof element.setElementStatus === 'function') {
        element.setElementStatus('ready');
      }
    },
    onError: (error) => {
      if (setIsPaypalClicked) setIsPaypalClicked(false);
      showLoader(false);
      console.error('Error in adyen component', error);
    },
  };
};

const createDropInPaypalConfig = (PayPalConfiguration) => ({
  ...PayPalConfiguration,
  icon: getIconPath('paypal-logo'),
  className: 'paypal',
});

const createDropInApplePayConfig = (ApplePayConfiguration) => ({
  ...ApplePayConfiguration,
  icon: getIconPath('apple-pay-logo'),
  className: 'apple-pay-icon',
});

const getPaymentMethodComponents = (isAdyenApplePayCheckoutEnabled, applePayVisibility) => {
  return isAdyenApplePayCheckoutEnabled && applePayVisibility
    ? [Card, PayPal, ApplePay]
    : [Card, PayPal];
};

const getDropInConfiguration = ({
  PayPalConfiguration,
  ApplePayConfiguration,
  onPayPalSelect,
  onApplePaySelect,
  paypalToBeSelected,
  checkoutRadioType,
  dropInRef,
  isAdyenApplePayCheckoutEnabled,
  applePayVisibility,
}) => {
  const ref = dropInRef?.current;
  const dataCardRadioType = ref?.getAttribute('data-cardRadioType');
  const paymentRadioType = dataCardRadioType || checkoutRadioType;
  const paymentMethodComponents = getPaymentMethodComponents(
    isAdyenApplePayCheckoutEnabled,
    applePayVisibility
  );

  const paymentMethodsConfiguration = {
    paypal: createDropInPaypalConfig(PayPalConfiguration),
    ...(isAdyenApplePayCheckoutEnabled && {
      applepay: createDropInApplePayConfig(ApplePayConfiguration),
    }),
  };

  return {
    showRadioButton: true,
    openFirstPaymentMethod: false,
    paymentMethodComponents,
    paymentMethodsConfiguration,
    onSelect: (paymentMethod) => {
      const name = paymentMethod?.props?.name || '';
      if (name === 'PayPal') onPayPalSelect?.('fromDropIn');
      if (name === 'Apple Pay' && isAdyenApplePayCheckoutEnabled) {
        onApplePaySelect?.('fromDropIn');
      }
    },
    onReady: () => {
      if (paypalToBeSelected || paymentRadioType === CheckoutConstants.PAY_PAL) {
        const paypalElement = document.querySelector(
          '.adyen-checkout__payment-method--paypal .adyen-checkout__payment-method__header__title'
        );

        if (paypalElement) {
          paypalElement.click();
        }
      }

      if (isAdyenApplePayCheckoutEnabled && paymentRadioType === CheckoutConstants.APPLEPAY_LABEL) {
        const applePayElement = document.querySelector(
          '.adyen-checkout__payment-method--applepay .adyen-checkout__payment-method__header__title'
        );
        if (applePayElement) {
          applePayElement.click();
        }
      }
    },
  };
};

const gettargetDiv = (isDesktop, divsArray) => {
  return isDesktop && divsArray.length > 1 ? divsArray[0] : divsArray[divsArray.length - 1];
};

const mountPayPalInstance = (
  checkout,
  PayPalConfiguration,
  ApplePayConfiguration,
  isAdyenApplePayCheckoutEnabled,
  applePayVisibility
) => {
  const { isDesktop } = getViewportInfo() || {};
  const paypalDivs = document.querySelectorAll(
    `#adyen-paypal-container-${isDesktop ? 'desktop' : 'mobile'}`
  );

  if (paypalDivs.length) {
    const targetDiv = gettargetDiv(isDesktop, paypalDivs);
    window.AdyenPaypalInstance = new PayPal(checkout, PayPalConfiguration);
    window.AdyenPaypalInstance.mount(targetDiv);
  }

  if (isAdyenApplePayCheckoutEnabled && applePayVisibility) {
    const applePayDivs = document.querySelectorAll(
      `#adyen-applepay-container-${isDesktop ? 'desktop' : 'mobile'}`
    );

    if (applePayDivs.length) {
      const targetApplePayDiv = gettargetDiv(isDesktop, applePayDivs);
      window.AdyenApplepayInstance = new ApplePay(checkout, ApplePayConfiguration);
      window.AdyenApplepayInstance.mount(targetApplePayDiv);
    }
  }
};

// Helper function to create shipping data
const createShippingData = ({
  errorLabels,
  shipModeId,
  shippingTotal,
  isDynamicFreeShippingEnabled,
  shipments,
  freeShippingThershold,
  orderType,
  productTotal,
}) => ({
  errorLabels: errorLabels || [],
  shipModeId: shipModeId || '',
  shippingTotal: shippingTotal || 0,
  isDynamicFreeShippingEnabled: isDynamicFreeShippingEnabled ?? false,
  shipments: shipments || [],
  freeShippingThershold: freeShippingThershold || 0,
  orderType: orderType || '',
  productTotal: productTotal || 0,
});

const AdyenPayment = ({
  cartValue,
  finalPriceValue,
  className,
  isBagPage,
  isDropIn,
  onPayPalSelect,
  onApplePaySelect,
  resetDropIn,
  orderType,
  heightValue,
  shipments,
  setPayPalModalActionOOS,
  startAdyenPayment,
  startAdyenApplePayInventory,
  confirmStartCheckoutAction,
  submitReview,
  adyenSessionData,
  setAdyenSetupData,
  adyenSetupData,
  setServerErrorCheckoutAction,
  setAddToCartError,
  paypalPaymentInProgressAction,
  setLoaderStateAction,
  setIsMixedPaymentWithPaypalAction,
  setIsPaypalClicked,
  reloadDropinPaypal,
  paypalToBeSelected,
  adyenIntentValue,
  isMiniBag,
  shippingContact,
  pageLedgerSummaryData,
  userProfileInfo,
  userBasketPickUpValues,
  userBasketPickUpAlternatePerson,
  errorLabels,
  checkoutRadioType,
  isAdyenApplePayCheckoutEnabled,
  emailAddressFromSS,
  freeShippingThershold,
  shipModeId,
  shippingTotal,
  isDynamicFreeShippingEnabled,
  productTotal,
  isApplePay,
  suppressEmailField,
  stores,
  isOptimizeApplePaySheetEnabled,
  isAdyenApplePayCheckoutVisibilityEnabled,
  waitForSessions,
  isBackPackAloneError,
  isBackPackOOS,
  bossGCAndPreOrderCase,
  verifyAllExpiredCoupons,
  toggleMessageModal,
  setCartLoading,
  adyenApplePayLoading,
  isSupressPaypalCrossCountryShipping,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  const componentRef = useRef(null);
  const applePayRef = useRef(null);
  const dropInRef = useRef(null);
  const bossGCAndPreOrderCaseRef = useRef(false);
  const isBackPackAloneErrorRef = useRef(false);
  const isBackPackOOSRef = useRef(false);
  const verifyAllExpiredCouponsRef = useRef(false);
  const dropInInstance = useRef(null);
  const isAdyenWebInitialized = useRef(false);
  const shippingPayloadRef = useRef(null);
  const shippingMethodsRef = useRef(null);
  const adyenSessionId = adyenSessionData && adyenSessionData.id;
  const prevAdyenSessionId = useRef(adyenSessionId);
  const { nextPublicClientKey, adyenEnv, country, locale } = getAPIConfig();
  const shippingData = createShippingData({
    errorLabels,
    shipModeId,
    shippingTotal,
    isDynamicFreeShippingEnabled,
    shipments,
    freeShippingThershold,
    orderType,
    productTotal,
  });

  const showLoader = (status) => {
    setLoaderStateAction(status);
    paypalPaymentInProgressAction(status);
  };

  const applePayVisibility =
    isAdyenApplePayCheckoutVisibilityEnabled &&
    isAdyenApplePayCheckoutVisibilityEnabled.includes(orderType);

  const initializeAdyenCheckout = async (newSessionData) => {
    const cartPrice = removeDecimal(finalPriceValue) || removeDecimal(cartValue);
    const countryCode = country || CONSTANTS?.DEFAULT_COUNTRY;
    const amount = parseAmount(cartPrice, countryCode);

    const session = newSessionData || adyenSessionData;

    const options = {
      clientKey: nextPublicClientKey,
      session: {
        id: session.id,
        sessionData: session.sessionData,
      },
      countryCode,
      amount,
      locale,
      environment: adyenEnv,
      analytics: {
        enabled: false,
      },
      onAdditionalDetails: async (state, component, actions) => {
        actions.resolve();
      },
      onPaymentCompleted() {},
    };

    return AdyenCheckout(options);
  };

  const getNeededConfigurations = (configArgs) => {
    const configs = {};
    // DropIn needs both if enabled, else only PayPal
    if (isDropIn) {
      configs.PayPalConfiguration = getPayPalConfiguration(configArgs);
      if (isAdyenApplePayCheckoutEnabled && applePayVisibility) {
        configs.ApplePayConfiguration = getApplePayConfiguration(configArgs);
      }
      return configs;
    }
    if (!isApplePay) {
      configs.PayPalConfiguration = getPayPalConfiguration(configArgs);
    }
    if (isApplePay) {
      configs.ApplePayConfiguration = getApplePayConfiguration({
        ...configArgs,
        componentRef: applePayRef,
      });
    }
    return configs;
  };

  const getShippingMethodCallNeeded = () => {
    const orderTypeValue =
      (isDropIn
        ? dropInRef?.current?.getAttribute(orderTypeKey)
        : componentRef?.current?.getAttribute(orderTypeKey)) || shippingData?.orderType;
    const isPureBossOrBopis = orderTypeValue === 'PURE_BOSS' || orderTypeValue === 'PURE_BOPIS';
    return (
      !isPureBossOrBopis &&
      (isApplePay || (isDropIn && isAdyenApplePayCheckoutEnabled && applePayVisibility))
    );
  };

  const loadAdyen = useCallback(
    async (newSessionData) => {
      showLoader(false);
      const checkout = await initializeAdyenCheckout(newSessionData);
      const zipCode = shippingContact?.postalCode;
      const state = shippingContact?.administrativeArea?.toUpperCase?.();
      let cachedShippingMethods = null;
      if (
        shippingMethodsRef.current &&
        shippingPayloadRef.current?.zipCode === zipCode &&
        shippingPayloadRef.current?.state === state
      ) {
        cachedShippingMethods = shippingMethodsRef.current;
      }
      shippingPayloadRef.current = { zipCode, state };
      const { newShippingMethods, shipmentMethods } = getShippingMethodCallNeeded()
        ? await fetchShippingMethods({
            shippingContact,
            shippingData,
            setCartLoading,
            cachedShippingMethods,
          })
        : {};
      shippingMethodsRef.current = shipmentMethods;
      setAdyenSetupData(checkout);

      const { ApplePayConfiguration, PayPalConfiguration } = getNeededConfigurations({
        heightValue,
        componentRef: isDropIn ? dropInRef : componentRef,
        orderType,
        checkout,
        session: adyenSessionData,
        shipments,
        isBagPage,
        setPayPalModalActionOOS,
        startAdyenPayment,
        confirmStartCheckoutAction,
        submitReview,
        setServerErrorCheckoutAction,
        setAddToCartError,
        showLoader,
        setIsMixedPaymentWithPaypalAction,
        setIsPaypalClicked,
        reloadDropinPaypal,
        adyenIntentValue,
        isMiniBag,
        shippingContact,
        userProfileInfo,
        userBasketPickUpValues,
        userBasketPickUpAlternatePerson,
        errorLabels,
        emailAddressFromSS,
        toggleMessageModal,
        bossGCAndPreOrderCaseRef,
        isBackPackAloneErrorRef,
        verifyAllExpiredCouponsRef,
        isBackPackOOSRef,
        loadAdyen,
        isSupressPaypalCrossCountryShipping,
        pageLedgerSummaryData,
        shippingMethods: newShippingMethods,
        startAdyenApplePayInventory,
        suppressEmailField,
        stores,
        isOptimizeApplePaySheetEnabled,
        setCartLoading,
        isDropIn,
        isApplePay,
        isAdyenApplePayCheckoutEnabled,
        applePayVisibility,
        shippingData,
      });

      if (isDropIn && dropInRef.current) {
        const dropInConfiguration = getDropInConfiguration({
          PayPalConfiguration,
          ApplePayConfiguration,
          onPayPalSelect,
          onApplePaySelect,
          paypalToBeSelected,
          checkoutRadioType,
          dropInRef,
          isAdyenApplePayCheckoutEnabled,
          applePayVisibility,
        });
        dropInInstance.current = new Dropin(checkout, dropInConfiguration);
        dropInInstance.current.mount(dropInRef.current);
        mountPayPalInstance(
          checkout,
          PayPalConfiguration,
          ApplePayConfiguration,
          isAdyenApplePayCheckoutEnabled,
          applePayVisibility
        );
      }

      if (componentRef.current && !isApplePay && PayPalConfiguration) {
        new PayPal(checkout, PayPalConfiguration).mount(componentRef.current);
      }
      if (applePayRef.current && isApplePay && ApplePayConfiguration) {
        new ApplePay(checkout, ApplePayConfiguration).mount(applePayRef.current);
        setCartLoading(true);
      }
    },
    [finalPriceValue, shipments, orderType, stores]
  );

  useEffect(() => {
    if (prevAdyenSessionId.current !== adyenSessionId) {
      // eslint-disable-next-line no-param-reassign
      waitForSessions.current = false;
      isAdyenWebInitialized.current = false;
      prevAdyenSessionId.current = adyenSessionId;
    }

    if (!isAdyenWebInitialized.current && finalPriceValue > 0 && !waitForSessions.current) {
      isAdyenWebInitialized.current = true;
      loadAdyen(adyenSessionData);
    }
    paypalPaymentInProgressAction(false);
  }, [loadAdyen, finalPriceValue, orderType, shipments, adyenSetupData, cartValue, adyenSessionId]);

  useEffect(() => {
    if (resetDropIn && dropInInstance.current) {
      dropInInstance.current.closeActivePaymentMethod();
    }
  }, [resetDropIn]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      const finalTimeout = setTimeout(() => {
        if (adyenApplePayLoading) setCartLoading(false);
      }, 300);

      return () => clearTimeout(finalTimeout);
    }, 5000);
    return () => clearTimeout(debounceTimer);
  }, [adyenApplePayLoading, finalPriceValue]);

  useEffect(() => {
    bossGCAndPreOrderCaseRef.current = bossGCAndPreOrderCase;
    isBackPackAloneErrorRef.current = isBackPackAloneError;
    verifyAllExpiredCouponsRef.current = verifyAllExpiredCoupons;
    isBackPackOOSRef.current = isBackPackOOS;
  }, [bossGCAndPreOrderCase, verifyAllExpiredCoupons, isBackPackOOS, isBackPackAloneError]);

  return (
    <div className={`paypal-adyen-container ${className}`}>
      {isDropIn ? (
        <div
          ref={dropInRef}
          key={`applepay-${finalPriceValue}`}
          id="dropin-container"
          data-finalprice={finalPriceValue}
          data-ordertype={orderType}
          data-shipments={JSON.stringify(shipments)}
          data-cardRadioType={checkoutRadioType}
          data-locator="adyen-dropin-container"
        />
      ) : (
        <>
          {!isApplePay && (
            <div
              ref={componentRef}
              id="paypal-container"
              data-finalprice={finalPriceValue}
              data-ordertype={orderType}
              data-shipments={JSON.stringify(shipments)}
            />
          )}
          {isApplePay && <div id="apple-pay-container" ref={applePayRef} />}
        </>
      )}
    </div>
  );
};

AdyenPayment.propTypes = {
  amount: PropTypes.number,
  currency: PropTypes.string,
};

AdyenPayment.defaultProps = {
  amount: 0,
  currency: 'USD',
};

export default withStyles(AdyenPayment, styles);
export { AdyenPayment as AdyenPaymentVanilla };
